// Local representation only; the server must still validate received bytes.
export const PHOTO_PREPARATION_POLICY = 'delivery-jpeg-2048-q95-v1';
export const MAX_PHOTO_BYTES = 10 * 1024 * 1024;
export const MAX_BATCH_BYTES = 50 * 1024 * 1024;
export const MAX_PHOTOS = 10;
const MAX_PIXELS = 50_000_000;
const refusal = () => new Error('This photo cannot be safely prepared here. The original is retained for review.');
const check = (signal) => { if (signal?.aborted) throw new DOMException('Preparation cancelled', 'AbortError'); };
const dimensions = (width, height) => {
  if (!width || !height || width * height > MAX_PIXELS) throw refusal();
  return { width, height };
};

// Only the SDR primary of a bounded, identified two-image HDR JPEG is admitted.
// General MPO/multi-view files remain refused. Original bytes are never rewritten.
function inspectHdrMpf(bytes, segmentStart, segmentEnd, tiff) {
  const fail = () => { throw refusal(); };
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const text = (start, end) => new TextDecoder().decode(bytes.subarray(start, end));
  const bounds = (start, length, end = segmentEnd) => { if (start < tiff || length < 0 || start + length > end) fail(); };
  bounds(tiff, 8);
  const endian = text(tiff, tiff + 2); const little = endian === 'II';
  if (!little && endian !== 'MM') fail();
  const u16 = p => { bounds(p, 2); return view.getUint16(p, little); };
  const u32 = p => { bounds(p, 4); return view.getUint32(p, little); };
  if (u16(tiff + 2) !== 42) fail();
  const ifd = tiff + u32(tiff + 4); if (ifd < tiff + 8) fail();
  const count = u16(ifd); if (count !== 3) fail();
  bounds(ifd, 2 + count * 12 + 4);
  const tags = new Map();
  for (let i = 0; i < count; i++) {
    const p = ifd + 2 + i * 12; const tag = u16(p);
    if (tags.has(tag)) fail(); tags.set(tag, p);
  }
  const tag = (id, type, length) => {
    const p = tags.get(id); if (p === undefined || u16(p + 2) !== type || u32(p + 4) !== length) fail();
    return p + 8;
  };
  const version = tag(0xb000, 7, 4);
  if (text(version, version + 4) !== '0100' || u32(tag(0xb001, 4, 1)) !== 2 || u32(ifd + 2 + count * 12) !== 0) fail();
  const entries = tiff + u32(tag(0xb002, 7, 32));
  if (entries < ifd + 2 + count * 12 + 4) fail(); bounds(entries, 32);
  const primaryAttributes = u32(entries); const secondaryAttributes = u32(entries + 16);
  // Permit only primary + undefined auxiliary, not stereo/panorama/other frames.
  if ((primaryAttributes & 0x5fffffff) !== 0x030000 || (secondaryAttributes & 0xbfffffff) !== 0) fail();
  for (const p of [entries + 12, entries + 14, entries + 28, entries + 30]) if (u16(p) > 2) fail();
  const primaryEnd = u32(entries + 4); const secondaryStart = tiff + u32(entries + 24);
  if (u32(entries + 8) !== 0 || primaryEnd <= segmentEnd || secondaryStart !== primaryEnd || secondaryStart + u32(entries + 20) !== bytes.length) fail();
  const scan = (start, end) => {
    if (bytes[start] !== 255 || bytes[start + 1] !== 216) fail();
    let offset = start + 2; let size; let scanned = false; let xmp;
    while (offset < end) {
      if (bytes[offset++] !== 255) fail();
      while (bytes[offset] === 255 && offset < end) offset++;
      const marker = bytes[offset++];
      if (marker === 217) { if (offset !== end || !size || !scanned) fail(); return { ...size, xmp }; }
      if (offset + 2 > end || marker === 0 || marker === 216 || (marker >= 208 && marker <= 215)) fail();
      const length = view.getUint16(offset);
      if (length < 2 || offset + length > end) fail();
      const payload = offset + 2;
      if (marker === 226 && text(payload, payload + 4) === 'MPF\0' && offset - 2 !== segmentStart) fail();
      if (marker === 225 && text(payload, payload + 29) === 'http://ns.adobe.com/xap/1.0/\0') {
        if (xmp !== undefined) fail(); xmp = text(payload + 29, offset + length);
      }
      if ([192, 193, 194].includes(marker)) {
        if (size || length < 8) fail();
        size = dimensions(view.getUint16(offset + 5), view.getUint16(offset + 3));
      }
      offset += length;
      if (marker === 218) {
        if (!size) fail(); scanned = true;
        while (offset < end) {
          if (bytes[offset] !== 255) { offset++; continue; }
          let next = offset + 1; while (next < end && bytes[next] === 255) next++;
          if (bytes[next] === 0 || (bytes[next] >= 208 && bytes[next] <= 215)) { offset = next + 1; continue; }
          break;
        }
      }
    }
    fail();
  };
  const primary = scan(0, primaryEnd); const secondary = scan(secondaryStart, bytes.length);
  if (!secondary.xmp || /<!DOCTYPE|<!ENTITY/i.test(secondary.xmp) || typeof DOMParser !== 'function') fail();
  const xml = new DOMParser().parseFromString(secondary.xmp, 'application/xml');
  if (xml.getElementsByTagName('parsererror').length) fail();
  const descriptions = [...xml.getElementsByTagNameNS('http://www.w3.org/1999/02/22-rdf-syntax-ns#', 'Description')];
  const property = (node, ns, name) => {
    const values = [...node.children].filter(e => e.namespaceURI === ns && e.localName === name);
    const attribute = node.getAttributeNS(ns, name);
    if (values.length > 1 || (attribute !== null && values.length) || values.some(e => e.children.length)) fail();
    return attribute ?? values[0]?.textContent?.trim();
  };
  const gainmaps = descriptions.filter(node => {
    const apple = property(node, 'http://ns.apple.com/pixeldatainfo/1.0/', 'AuxiliaryImageType') === 'urn:com:apple:photo:2020:aux:hdrgainmap'
      && /^\d+$/.test(property(node, 'http://ns.apple.com/HDRGainMap/1.0/', 'HDRGainMapVersion') || '');
    const adobeNs = 'http://ns.adobe.com/hdr-gain-map/1.0/';
    const adobe = property(node, adobeNs, 'Version') === '1.0'
      && [undefined, 'False', 'false'].includes(property(node, adobeNs, 'BaseRenditionIsHDR'));
    return apple || adobe;
  });
  if (gainmaps.length !== 1) fail();
  return { width: primary.width, height: primary.height, decodeRanges: [[0, segmentStart], [segmentEnd, primaryEnd]] };
}

// Unknown containers (including HEIF and animated GIF/WebP/PNG) remain refused.
export function inspectPreparationSource(buffer) {
  const bytes = new Uint8Array(buffer);
  const view = new DataView(buffer);
  if (bytes.length >= 33 && [137, 80, 78, 71, 13, 10, 26, 10].every((b, i) => bytes[i] === b)) {
    let size; let ended = false;
    for (let offset = 8; offset + 12 <= bytes.length;) {
      const length = view.getUint32(offset);
      if (length > bytes.length - offset - 12) throw refusal();
      const type = String.fromCharCode(...bytes.subarray(offset + 4, offset + 8));
      if (offset === 8 && (type !== 'IHDR' || length !== 13)) throw refusal();
      if (type === 'acTL' || type === 'fcTL' || type === 'fdAT') throw refusal();
      if (type === 'IHDR') {
        if (size || length !== 13) throw refusal();
        size = dimensions(view.getUint32(offset + 8), view.getUint32(offset + 12));
      }
      offset += length + 12;
      if (type === 'IEND') { ended = length === 0 && offset === bytes.length; break; }
    }
    if (!size || !ended) throw refusal();
    return size;
  }
  if (bytes[0] === 255 && bytes[1] === 216) {
    let size;
    for (let offset = 2; offset + 4 <= bytes.length;) {
      if (bytes[offset++] !== 255) throw refusal();
      while (bytes[offset] === 255) offset += 1;
      const marker = bytes[offset++];
      if (offset + 2 > bytes.length) throw refusal();
      const length = view.getUint16(offset);
      if (length < 2 || offset + length > bytes.length) throw refusal();
      if (marker === 226 && length >= 6 && String.fromCharCode(...bytes.subarray(offset + 2, offset + 6)) === 'MPF\0') return inspectHdrMpf(bytes, offset - 2, offset + length, offset + 6);
      if ([192, 193, 194].includes(marker)) {
        if (size || length < 8) throw refusal();
        size = dimensions(view.getUint16(offset + 5), view.getUint16(offset + 3));
      }
      if (marker === 218) {
        if (!size) throw refusal();
        return size; // The native decoder validates the compressed still image.
      }
      offset += length;
    }
  }
  throw refusal();
}

export function validatePreparationBatch(photos) {
  if (photos.length > MAX_PHOTOS) throw new Error('Maximum 10 photos. Your existing photos are retained.');
  let bytes = 0;
  for (const photo of photos) {
    if (!(photo instanceof Blob) || !photo.size || photo.size > MAX_PHOTO_BYTES) throw new Error('Each original photo must be nonempty and no larger than 10 MiB.');
    bytes += photo.size;
  }
  if (bytes > MAX_BATCH_BYTES) throw new Error('Original photos must total no more than 50 MiB.');
}

export async function prepareDeliveryPhoto(file, { signal } = {}) {
  validatePreparationBatch([file]);
  check(signal);
  if (typeof globalThis.createImageBitmap !== 'function') throw refusal();
  const sourceBytes = await file.arrayBuffer();
  const sourceInfo = inspectPreparationSource(sourceBytes);
  const decodeFile = sourceInfo.decodeRanges
    ? new Blob(sourceInfo.decodeRanges.map(([start, end]) => new Uint8Array(sourceBytes, start, end - start)), { type: 'image/jpeg' }) : file;
  check(signal);
  let bitmap; let canvas;
  try {
    bitmap = await globalThis.createImageBitmap(decodeFile, { imageOrientation: 'from-image', colorSpaceConversion: 'default' });
    check(signal);
    dimensions(bitmap.width, bitmap.height);
    const scale = Math.min(1, 2048 / Math.max(bitmap.width, bitmap.height));
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));
    canvas = document.createElement('canvas');
    canvas.width = width; canvas.height = height;
    const context = canvas.getContext('2d', { colorSpace: 'srgb', alpha: false });
    if (!context) throw refusal();
    context.fillStyle = '#fff';
    context.fillRect(0, 0, width, height);
    context.drawImage(bitmap, 0, 0, width, height);
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.95));
    check(signal);
    if (!blob || blob.type !== 'image/jpeg' || !blob.size || blob.size > MAX_PHOTO_BYTES) throw refusal();
    return { file: new File([blob], 'delivery-photo.jpg', { type: 'image/jpeg' }), width, height, policy: PHOTO_PREPARATION_POLICY };
  } finally {
    bitmap?.close();
    if (canvas) { canvas.width = 0; canvas.height = 0; }
  }
}
